/**
 * HandCardFace — the shared inner face of a hand card (the M footprint, 02 §3.1):
 * the cost coin in the art window's corner, the art window, the name row and the
 * combat chips, on the one card skin. It mirrors the field card's order (window,
 * name, chips) so the laydown flight barely has to morph anything. Rendered
 * inside the real hand card, the sandbox catalog, the floating drag preview and
 * the return-flight ghost.
 */
import type { ReactElement } from 'react';
import { memo } from 'react';
import type { CardType } from '@cards/engine';
import { CardGlyph } from './CardGlyph.js';
import { StatChip } from './StatChip.js';
import { ScrollingCardName } from './ScrollingCardName.js';
import { cardName } from './locale.js';

export interface HandCardFaceProps {
  defId: string;
  cost: number | undefined;
  type: CardType | undefined;
  hasStats: boolean;
  attack?: number;
  health?: number;
  /** When set, the stat chips get `${prefix}-damage` / `${prefix}-health` test ids. */
  statTestIdPrefix?: string;
  glyphSize?: number;
  /** false for faces inside per-frame-resized previews (see ScrollingCardName). */
  nameAnimated?: boolean;
}

/**
 * Memoised: every prop is a primitive, and the face is the heavy part of a card
 * (glyph, icons, scrolling name). A hand reorder mid-drag re-renders the Hand on
 * each frame the placeholder moves; without this, all twelve faces rebuilt with it.
 */
export const HandCardFace = memo(function HandCardFace({
  defId,
  cost,
  hasStats,
  attack,
  health,
  statTestIdPrefix,
  glyphSize = 60,
  nameAnimated = true,
}: HandCardFaceProps): ReactElement {
  return (
    <>
      <div
        className="cards-hand-cost"
        aria-label={`Энергия: ${cost ?? '–'}`}
      >
        <StatChip icon="energy" value={cost ?? '–'} aria-hidden="true" />
      </div>
      <div className="cards-hand-glyph">
        <CardGlyph defId={defId} size={glyphSize} />
      </div>
      <ScrollingCardName
        name={cardName(defId)}
        className="cards-hand-name"
        trackClassName="cards-hand-name-track"
        animate={nameAnimated}
      />
      {hasStats && attack !== undefined && health !== undefined ? (
        <div className="cards-hand-stats">
          <StatChip
            icon="attack"
            value={attack}
            {...(statTestIdPrefix === undefined ? {} : { 'data-testid': `${statTestIdPrefix}-damage` })}
            aria-label={`Атака: ${attack}`}
            title={`Атака: ${attack}`}
          />
          <StatChip
            icon="health"
            value={health}
            {...(statTestIdPrefix === undefined ? {} : { 'data-testid': `${statTestIdPrefix}-health` })}
            aria-label={`Здоровье: ${health}`}
            title={`Здоровье: ${health}`}
          />
        </div>
      ) : null}
    </>
  );
});
